import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Button, Select, Grid, TextField, MenuItem, TableCell,
  TableRow, Table, TableHead, TableBody, FormControlLabel,
  Checkbox, Typography, Popover, TableContainer, Paper, FormControl, Pagination,
  Menu, Autocomplete, Snackbar, Alert, Stack
} from '@mui/material';
import dayjs from 'dayjs';
import { useDispatch, useSelector } from 'react-redux';
import { searchLeadReportAction } from '../../redux/actions/leadActions';
import DatePickerField from "../../common/DatePickerDashboard";
import { useTheme, useMediaQuery } from '@mui/material';
import NoDataFound from '../../common/NoDataFound';
import NoSearchResults from '../../common/helpers/NoSearchResults';
import { exportLeadReportAction } from '../../redux/actions/reportActions';
import { formatPhoneNumber } from '../../common/helpers/commonFun';
import PhoneNumberField from '../../common/PhoneNumberField';
import debounce from 'lodash.debounce';
import { searchAgentAction } from "../../redux/actions/userActions";

const CustomerCrmLead = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const leadsValue = useSelector(state => state.leads.searchLeadsReport || {});
  const search_agent_list = useSelector((state) => state.user.search_agent || {});
  const agentOptions = search_agent_list.content || [];
  const columnToggleRef = useRef(null);

  const searchLeads = leadsValue?.content || [];
  const totalElements = leadsValue?.totalElements || 0;
  const totalPages = leadsValue?.totalPages || 0;

  const [showColumnToggle, setShowColumnToggle] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [touchedFields, setTouchedFields] = useState({});
  const [hasSearched, setHasSearched] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [searchError, setSearchError] = useState('');
  const [agentSearchTerm, setAgentSearchTerm] = useState('');
  const [createdBySearchTerm, setCreatedBySearchTerm] = useState('');
  const [sortField, setSortField] = useState('');
  const [sortOrder, setSortOrder] = useState('asc');
  const [resetCount, setResetCount] = useState(0);

  const get_all_location = useSelector(
   (state) => state?.agency?.get_all_locations_list || []);

  const [downloadAnchorEl, setDownloadAnchorEl] = useState(null);
  const isDownloadOpen = Boolean(downloadAnchorEl);
  const [exportType, setExportType] = useState("pdf");
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info',
  });

  const [dateType, setDateType] = useState("");
  const [dateRange, setDateRange] = useState({ from: null, to: null });

  const defaultColumns = {
    leadName: true,
    location: true,
    leadSource: true,
    phoneNumber: true,
    email: true,
    status: true,
    quoteNumber: true,
    createdDate: true,
    updatedDate: true,
    assignedTo: true, 
    createdBy: true, 
  };

  const [visibleColumns, setVisibleColumns] = useState(defaultColumns);

const [filters, setFilters] = useState({
    leadName: '',
    email: '',
    phoneNumber: '',
    location: null,
    zipCode: '',
    assignedTo: null, 
    leadSource: null,
    lineOfBusiness: null,
    status: null,
    quoteNumber: '',
    createdBy: null, 
    createdFrom: '',
    createdTo: '',
    updatedFrom: '',
    updatedTo: '',
  });

  const validateEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.(com|in|org)$/i.test(email);
  };

  // Agent search function for assignedTo
  const handleAgentSearch = debounce((value) => {
    if (value?.length > 1) {
      dispatch(searchAgentAction(value));
    }
  }, 300);

  // Agent search function for createdBy
  const handleCreatedBySearch = debounce((value) => {
    if (value?.length > 1) {
      dispatch(searchAgentAction(value));
    }
  }, 300);

  useEffect(() => {
    const saved = localStorage.getItem('crm_lead_visible_columns');
    if (saved) {
      setVisibleColumns(JSON.parse(saved));
    }
  }, []);

  useEffect(() => {
    localStorage.setItem('crm_lead_visible_columns', JSON.stringify(visibleColumns));
  }, [visibleColumns]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (columnToggleRef.current && !columnToggleRef.current.contains(event.target)) {
        setShowColumnToggle(false);
      }
    };

    if (showColumnToggle) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showColumnToggle]);

  const handleChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

const buildCleanExportFilters = () => {
    const cleaned = Object.entries(filters).reduce((acc, [key, value]) => {
      if (value !== "" && value !== null && value !== undefined && value !== "All") {
        // Convert assignedTo and createdBy to numbers if they are ID objects
        if (key === 'assignedTo' && typeof value === 'object' && value.id) {
          acc[key] = value.id;
        } else if (key === 'createdBy' && typeof value === 'object' && value.id) {
          acc[key] = value.id;
        } else {
          acc[key] = value;
        }
      }
      return acc;
    }, {});

    if (dateType === "created Date") {
      if (dateRange.from) cleaned["createdFrom"] = dayjs(dateRange.from).format("YYYY-MM-DD");
      if (dateRange.to) cleaned["createdTo"] = dayjs(dateRange.to).format("YYYY-MM-DD");
    }
    if (dateType === "last Modified Date") {
      if (dateRange.from) cleaned["updatedFrom"] = dayjs(dateRange.from).format("YYYY-MM-DD");
      if (dateRange.to) cleaned["updatedTo"] = dayjs(dateRange.to).format("YYYY-MM-DD");
    }

    if (cleaned.phoneNumber) {
      cleaned.phoneNumber = "+1" + String(cleaned.phoneNumber).replace(/\D/g, "");
    }

    return cleaned;
  };

  const handleDownloadClick = (e) => setDownloadAnchorEl(e.currentTarget);
  const handleDownloadClose = () => setDownloadAnchorEl(null);

  const handleExport = async (fmt) => {
    const formatToUse = fmt || exportType;
    try {
      setSnackbar({
        open: true,
        message: `Generating ${formatToUse.toUpperCase()}...`,
        severity: 'info',
      });

      const cleaned = buildCleanExportFilters();

      await dispatch(exportLeadReportAction(cleaned, formatToUse));

      setSnackbar({
        open: true,
        message: `${formatToUse.toUpperCase()} exported successfully!`,
        severity: 'success',
      });
    } catch (e) {
      console.error("CRM Lead export failed:", e);
      setSnackbar({
        open: true,
        message: `Failed to export ${formatToUse.toUpperCase()}. Please try again.`,
        severity: 'error',
      });
    } finally {
      handleDownloadClose();
    }
  };

  const handleReset = () => {
    setFilters({
      leadName: '',
      email: '',
      phoneNumber: '',
      location: null,
      zipCode: '',
      assignedTo: null, 
      leadSource: null,
      lineOfBusiness: null,
      status: null,
      quoteNumber: '',
      createdBy: null, 
      createdFrom: '',
      createdTo: '',
      updatedFrom: '',
      updatedTo: '',
    });
    setAgentSearchTerm('');
    setCreatedBySearchTerm('');
    setFieldErrors({});
    setTouchedFields({});
    setHasSearched(false);
    setPage(0);
    setSearchError('');
    setDateType("");
    setDateRange({ from: null, to: null });
    setSortField('');
    setSortOrder('asc');
    setResetCount(prev => prev + 1);
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));

    if (name === 'email') {
      if (value.length > 0 && !validateEmail(value)) {
        setFieldErrors(prev => ({
          ...prev,
          email: 'Invalid email format',
        }));
      } else {
        setFieldErrors(prev => ({ ...prev, email: '' }));
      }
      setTouchedFields(prev => ({ ...prev, email: true }));
    }
  };

  const handleSubmit = (pageNumber = 0, size = rowsPerPage, sortFieldParam = sortField, sortOrderParam = sortOrder) => {
    const hasAnyFilter = Object.values(filters).some(
      value => value !== '' && value !== null && value !== undefined
    ) ||
      (dateType && (dateRange.from || dateRange.to));

    if (!hasAnyFilter) {
      setSearchError("Please fill at least one search field.");
      return;
    }

    setSearchError("");

    if (filters.email && !validateEmail(filters.email)) {
      setFieldErrors(prev => ({ ...prev, email: 'Invalid email format' }));
      setTouchedFields(prev => ({ ...prev, email: true }));
      return;
    }

    setSearchError('');

    const cleanedFilters = Object.entries(filters).reduce((acc, [key, value]) => {
      if (value !== '' && value !== null && value !== undefined && value !== 'All') {
        // Convert assignedTo and createdBy to numbers if they are ID objects
        if (key === 'assignedTo' && typeof value === 'object' && value.id) {
          acc[key] = value.id;
        } else if (key === 'createdBy' && typeof value === 'object' && value.id) {
          acc[key] = value.id;
        } else {
          acc[key] = value;
        }
      }
      return acc;
    }, {});

    if (dateType === "created Date") {
      if (dateRange.from) cleanedFilters["createdFrom"] = dateRange.from;
      if (dateRange.to) cleanedFilters["createdTo"] = dateRange.to;
    }

    if (dateType === "last Modified Date") {
      if (dateRange.from) cleanedFilters["updatedFrom"] = dateRange.from;
      if (dateRange.to) cleanedFilters["updatedTo"] = dateRange.to;
    }

    if (cleanedFilters.phoneNumber) {
      cleanedFilters.phoneNumber = '+1' + cleanedFilters.phoneNumber.replace(/\D/g, '');
    }

    dispatch(searchLeadReportAction(cleanedFilters, pageNumber, size, sortFieldParam, sortOrderParam));
    setHasSearched(true);
  };

const handleSearch = () => {
  const fieldsToValidate = ['leadName', 'phoneNumber', 'zipCode', 'location'];
  const hasErrors = fieldsToValidate.some(field => {
    const value = filters[field];
    // Check if value exists and is a string before checking length
    return value && typeof value === 'string' && value.length > 0 && value.length < 3;
  });

  if (hasErrors) {
    const newErrors = {};
    fieldsToValidate.forEach(field => {
      const value = filters[field];
      if (value && typeof value === 'string' && value.length > 0 && value.length < 3) {
        newErrors[field] = `${field.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())} must be at least 3 characters`;
      }
    });
    setFieldErrors(prev => ({ ...prev, ...newErrors }));
    setTouchedFields(prev => ({
      ...prev,
      leadName: true,
      email: true,
      phoneNumber: true,
      zipCode: true,
      location: true,
    }));
    return;
  }

  setPage(0);
  handleSubmit(0, rowsPerPage);
};

  const handlePageChange = (event, value) => {
    const newPage = value - 1;
    setPage(newPage);
    handleSubmit(newPage, rowsPerPage);
  };

  const handleRowsPerPageChange = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    if (hasSearched) {
      handleSubmit(0, newRowsPerPage);
    }
  };

  const handleFilterClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleFilterClose = () => {
    setAnchorEl(null);
  };

  const handleSort = (field) => {
    const newOrder = sortField === field && sortOrder === 'asc' ? 'desc' : 'asc';
    setSortField(field);
    setSortOrder(newOrder);
    setPage(0);

    if (!hasSearched) return;
    handleSubmit(0, rowsPerPage, field, newOrder);
  };

  const open = Boolean(anchorEl);
  const popoverId = open ? 'filter-popover' : undefined;

  // Fix for duplicate location options - create unique locations
  const uniqueLocations = Array.from(new Map(
    get_all_location.map(item => [item.locationDescription, item])
  ).values());

  return (
    <Box sx={{
      padding: isMobile ? '8px' : '24px',
      overflowX: 'auto',
      backgroundColor: '#ffffff',
      minHeight: '100vh',
      marginBottom: '120px',
      '@media (min-width: 768px) and (max-width: 1024px)': {
        minHeight: '40vh',
        height: 'auto',
        overflowY: 'auto',
      },
      '@media (min-width: 1024px) and (min-height: 1366px)': {
        height: 'auto',
        minHeight: '80vh',
      },
    }}>
      {/* Header Section */}
      <Box sx={{
        display: "flex",
        flexDirection: isMobile ? "column" : "row",
        justifyContent: "space-between",
        alignItems: isMobile ? "flex-start" : "center",
        marginBottom: '16px',
        flexWrap: 'wrap',
        gap: 2
      }}>
        <Typography 
          variant={isMobile ? "h6" : "h2"} sx={{          
            margin: isMobile ? "10px 0" : "30px 0 10px 0",
            ml: 2
          }}
        >
          Report: Customer CRM Lead Status
          {hasSearched && ` (${totalElements.toLocaleString() || 0})`}
        </Typography>

        <Box display="flex" gap={2} sx={{ mt: isMobile ? 1 : 0 }}>
          <Button
            variant="contained"
            aria-controls={isDownloadOpen ? 'crm-download-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={isDownloadOpen ? 'true' : undefined}
            onClick={handleDownloadClick}
            disabled={!hasSearched}
          >
            Download
          </Button>


          <Menu
            id="crm-download-menu"
            anchorEl={downloadAnchorEl}
            open={isDownloadOpen}
            onClose={handleDownloadClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <MenuItem onClick={() => handleExport('pdf')}>PDF</MenuItem>
            <MenuItem onClick={() => handleExport('xlsx')}>Excel (XLSX)</MenuItem>
            <MenuItem onClick={() => handleExport('csv')}>CSV</MenuItem>
          </Menu>

          <Button
            variant="outlined"
            onClick={handleFilterClick}
          >
            Filter
          </Button>

          <Button
            variant="outlined"
            onClick={() => setShowColumnToggle((prev) => !prev)}
          >
            Select Columns
          </Button>

        </Box>
      </Box>

      {/* Column Toggle Dropdown */}
      {showColumnToggle && (
        <Box
          ref={columnToggleRef}
          sx={{
            position: 'absolute',
            top: isMobile ? 180 : 200,
            right: isMobile ? 10 : 30,
            zIndex: 10,
            backgroundColor: 'white',
            border: '1px solid #ccc',
            borderRadius: 2,
            padding: 2,
            boxShadow: 3,
            maxHeight: 400,
            overflowY: 'auto',
            maxWidth: isMobile ? '180px' : '200px'
          }}
        >
          <Typography fontWeight={600} mb={1}>Select Columns</Typography>
          {Object.keys(defaultColumns).map((column) => (
            <FormControlLabel
              key={column}
              control={
                <Checkbox
                  checked={visibleColumns[column]}
                  onChange={() =>
                    setVisibleColumns(prev => ({
                      ...prev,
                      [column]: !prev[column],
                    }))
                  }
                  size={isMobile ? "small" : "medium"}
                />
              }
              label={
                <Typography fontSize={isMobile ? "12px" : "14px"}>
                  {column === 'assignedTo' ? 'Agent Name' :
                   column === 'createdBy' ? 'Created By' :
                   column.replace(/([A-Z])/g, ' $1').replace(/^./, (str) => str.toUpperCase())}
                </Typography>
              }
            />
          ))}
        </Box>
      )}

      {/* Main Search Fields - Outside Filter */}
      <Box mb={3} p={2} sx={{ border: '1px solid #e0e0e0', borderRadius: '8px' }}>
        <Grid container spacing={3} alignItems="flex-start">
          <Grid item xs={12} sm={2} md={1.8}>
            <Box>
              <label style={{ fontSize: 14, display: 'block', marginBottom: 6 }}>
                Lead Name
              </label>
              <TextField
                name="leadName"
                size={isMobile ? "medium" : "medium"}
                value={filters.leadName}
                onChange={handleFilterChange}
                onBlur={() => {
                  setTouchedFields(prev => ({ ...prev, leadName: true }));
                  if (filters.leadName.length > 0 && filters.leadName.length < 3) {
                    setFieldErrors(prev => ({
                      ...prev,
                      leadName: 'Lead Name must be at least 3 characters',
                    }));
                  } else {
                    setFieldErrors(prev => ({ ...prev, leadName: '' }));
                  }
                }}
                error={touchedFields.leadName && !!fieldErrors.leadName}
                helperText={touchedFields.leadName && fieldErrors.leadName ? fieldErrors.leadName : ' '}
                sx={{
                  width: '100%',
                  '& .MuiInputBase-root': {
                    height: isMobile ? '48px' : '56px',
                  },
                  '& .MuiInputBase-input': {
                    fontSize: 14,
                  },
                  '& .MuiFormHelperText-root': {
                    fontSize: 12,
                    minHeight: '18px',
                    marginTop: 0,
                  },
                }}
              />
            </Box>
          </Grid>

          <Grid item xs={12} sm={2} md={1.8}>
            <Box>
              <label style={{ fontSize: 14, display: 'block', marginBottom: 6 }}>
                Email
              </label>
              <TextField
                name="email"
                size={isMobile ? "medium" : "medium"}
                value={filters.email}
                onChange={handleFilterChange}
                onBlur={() => {
                  setTouchedFields(prev => ({ ...prev, email: true }));
                  if (filters.email.length > 0 && !validateEmail(filters.email)) {
                    setFieldErrors(prev => ({
                      ...prev,
                      email: 'Invalid email format',
                    }));
                  } else {
                    setFieldErrors(prev => ({ ...prev, email: '' }));
                  }
                }}
                error={touchedFields.email && !!fieldErrors.email}
                helperText={touchedFields.email && fieldErrors.email ? fieldErrors.email : ' '}
                sx={{
                  width: '100%',
                  '& .MuiInputBase-root': {
                    height: isMobile ? '48px' : '56px',
                  },
                  '& .MuiInputBase-input': {
                    fontSize: 14,
                  },
                  '& .MuiFormHelperText-root': {
                    fontSize: 12,
                    minHeight: '18px',
                    marginTop: 0,
                  },
                }}
              />
            </Box>
          </Grid>

          <Grid item xs={12} sm={2} md={1.8}>
            <Box>
              <PhoneNumberField
                name="phoneNumber"
                size={isMobile ? "medium" : "medium"}
                value={filters.phoneNumber}
                onChange={(e) => {
                  let rawValue = e.target.value.replace(/\D/g, '');
                  if (rawValue.length > 10) rawValue = rawValue.slice(0, 10);
                  let formattedValue = rawValue;
                  if (rawValue.length > 6) {
                    formattedValue = `${rawValue.slice(0, 3)}-${rawValue.slice(3, 6)}-${rawValue.slice(6)}`;
                  } else if (rawValue.length > 3) {
                    formattedValue = `${rawValue.slice(0, 3)}-${rawValue.slice(3)}`;
                  }
                  setFilters(prev => ({ ...prev, phoneNumber: formattedValue }));
                }}
                onBlur={() => {
                  setTouchedFields(prev => ({ ...prev, phoneNumber: true }));
                  if (filters.phoneNumber && filters.phoneNumber.replace(/\D/g, '').length !== 10) {
                    setFieldErrors(prev => ({ ...prev, phoneNumber: 'Phone number must be 10 digits' }));
                  } else {
                    setFieldErrors(prev => ({ ...prev, phoneNumber: '' }));
                  }
                }}
                sx={{
                  width: '100%',
                  '& .MuiInputBase-root': {
                    height: isMobile ? '48px' : '56px',
                  },
                  '& .MuiInputBase-input': {
                    fontSize: 14,
                  },
                  '& .MuiFormHelperText-root': {
                    fontSize: 12,
                    minHeight: '18px',
                    marginTop: 0,
                  },
                }}
                label="Phone Number"
                labelStyle={{ fontSize: 14, fontWeight: 400, mb: 1 }}
                required={false}
              />
            </Box>
          </Grid>

          <Grid item xs={12} sm={2} md={1.8}>
            <Box>
              <label style={{ fontSize: 14, display: 'block', marginBottom: 6 }}>
                ZipCode
              </label>
              <TextField
                name="zipCode"
                size={isMobile ? "medium" : "medium"}
                value={filters.zipCode}
                onChange={handleFilterChange}
                onBlur={() => {
                  setTouchedFields(prev => ({ ...prev, zipCode: true }));
                  if (filters.zipCode.length > 0 && filters.zipCode.length < 5) {
                    setFieldErrors(prev => ({
                      ...prev,
                      zipCode: 'ZipCode must be at least 5 characters',
                    }));
                  } else {
                    setFieldErrors(prev => ({ ...prev, zipCode: '' }));
                  }
                }}
                error={touchedFields.zipCode && !!fieldErrors.zipCode}
                helperText={touchedFields.zipCode && fieldErrors.zipCode ? fieldErrors.zipCode : ' '}
                sx={{
                  width: '100%',
                  '& .MuiInputBase-root': {
                    height: isMobile ? '48px' : '56px',
                  },
                  '& .MuiInputBase-input': {
                    fontSize: 14,
                  },
                  '& .MuiFormHelperText-root': {
                    fontSize: 12,
                    minHeight: '18px',
                    marginTop: 0,
                  },
                }}
                inputProps={{
                  maxLength: 5,
                  inputMode: 'numeric',
                  pattern: '[0-9]*'
                }}
                onInput={(e) => {
                  e.target.value = e.target.value.replace(/\D/g, '').slice(0, 6);
                }}
              />
            </Box>
          </Grid>

          <Grid item xs={12} sm={6} md={1.8}>
            <label style={{ fontSize: 14, fontWeight: 500, display: 'block', marginBottom: 6 }}>
              Location
            </label>
            <Autocomplete
              freeSolo
              key={`location-${resetCount}`}
              options={uniqueLocations}
              getOptionLabel={(option) =>
                typeof option === 'string'
                  ? option
                  : option.locationDescription || ''
              }
              isOptionEqualToValue={(option, value) => {
                if (!option || !value) return false;
                const optionValue = typeof option === 'string' ? option : option.locationDescription;
                const valueValue = typeof value === 'string' ? value : value.locationDescription;
                return optionValue === valueValue;
              }}
              renderOption={(props, option) => {
                const { key, ...otherProps } = props;
                return (
                  <li key={option.id || option.locationDescription} {...otherProps}>
                    {option.locationDescription}
                  </li>
                );
              }}
              onInputChange={(e, newInputValue) => {
                setFilters((prev) => ({
                  ...prev,
                  location: newInputValue
                }));
              }}
              onChange={(e, newValue) => {
                setFilters((prev) => ({
                  ...prev,
                  location: newValue?.locationDescription || newValue || ''
                }));
              }}
              value={filters.location || null}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size={isMobile ? "small" : "medium"}
                  placeholder="Select Location"
                  sx={{
                    width: '100%',
                    '& .MuiInputBase-root': {
                      height: isMobile ? '40px' : '55px'
                    }
                  }}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={4} md={3} mt={-0.8}>
           <Box
              display="flex"
              gap={2}
              justifyContent="flex-start"
              alignItems="center"
              sx={{
                mt: isMobile ? 0 : '34px',
                ml: 0,
              }}
            >
              <Button
                variant="contained"
                onClick={handleSearch}
              >
                Search
              </Button>

              <Button
                variant="outlined"
                onClick={handleReset}
              >
                Reset
              </Button>
            </Box>

          </Grid>
        </Grid>
        {searchError && (
          <Typography
            variant="caption"
            color="error"
            sx={{ display: 'block', mt: 0.5 }}
          >
            {searchError}
          </Typography>
        )}
      </Box>

      {/* Filter Popover - Additional Fields */}
      <Popover
        id={popoverId}
        open={open}
        anchorEl={anchorEl}
        onClose={handleFilterClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        sx={{
          '& .MuiPaper-root': {
            width: isMobile ? '100%' : 'auto',
            maxWidth: isMobile ? '100%' : '1000px'
          }
        }}
      >
        <Box sx={{ p: 2 }}>
          <Grid container spacing={2}>
            {/* Date Fields */}
            <Grid item xs={12} sm={6} md={3}>
              <label style={{ fontSize: 14, fontWeight: 500, display: 'block', marginBottom: 4 }}>
                Date Type
              </label>
              <Select
                fullWidth
                value={dateType}
                onChange={(e) => {
                  setDateType(e.target.value);
                  setDateRange({ from: null, to: null });
                }}
                displayEmpty
              >
                <MenuItem value="">Select Date Type</MenuItem>
                <MenuItem value="created Date">Created Date</MenuItem>
                <MenuItem value="last Modified Date">Last Modified Date</MenuItem>
              </Select>
            </Grid>

            {dateType && (
              <>
                <Grid item xs={12} sm={6} md={3}>
                  <label style={{ fontSize: 14, fontWeight: 500, display: 'block', marginBottom: 4 }}>
                    {dateType.charAt(0).toUpperCase() + dateType.slice(1)} From
                  </label>
                  <DatePickerField
                    fieldName="from"
                    formData={dateRange}
                    handleDateChange={(name, value) =>
                      setDateRange((prev) => ({ ...prev, from: value }))
                    }
                    handleBlur={() => { }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <label style={{ fontSize: 14, fontWeight: 500, display: 'block', marginBottom: 4 }}>
                    {dateType.charAt(0).toUpperCase() + dateType.slice(1)} To
                  </label>
                  <DatePickerField
                    fieldName="to"
                    formData={dateRange}
                    handleDateChange={(name, value) =>
                      setDateRange((prev) => ({ ...prev, to: value }))
                    }
                  />
                </Grid>
              </>
            )}

            {/* Assigned To Field (Agent Name) */}
            <Grid item xs={12} sm={6} md={3}>
              <label style={{ fontSize: 14, fontWeight: 500, display: 'block', marginBottom: 4 }}>
                Agent Name (assignedTo)
              </label>
              <Autocomplete
                options={agentOptions}
                getOptionLabel={(option) =>
                  typeof option === 'string'
                    ? option
                    : `${option.firstName || ''} ${option.lastName || ''}`
                }
                isOptionEqualToValue={(option, value) => {
                  if (!option || !value) return false;
                  if (typeof option === 'string' && typeof value === 'string') {
                    return option === value;
                  }
                  if (typeof option === 'object' && typeof value === 'object') {
                    return option.id === value.id;
                  }
                  return false;
                }}
                renderOption={(props, option) => {
                  const { key, ...otherProps } = props;
                  return (
                    <li key={option.id} {...otherProps}>
                      {option.firstName} {option.lastName}
                    </li>
                  );
                }}
                onInputChange={(e, newInputValue) => {
                  setAgentSearchTerm(newInputValue);
                  handleAgentSearch(newInputValue);
                }}
                onChange={(e, newValue) => {
                  setFilters((prev) => ({
                    ...prev,
                    assignedTo: newValue || '' 
                  }));
                }}
                value={filters.assignedTo || null}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size={isMobile ? "small" : "medium"}
                    placeholder="Search Agent"
                    sx={{
                      width: '100%',
                      '& .MuiInputBase-root': {
                        height: isMobile ? '40px' : '55px'
                      }
                    }}
                  />
                )}
              />
            </Grid>

            {/* Created By Field */}
            <Grid item xs={12} sm={6} md={3}>
              <label style={{ fontSize: 14, fontWeight: 500, display: 'block', marginBottom: 4 }}>
                Created By
              </label>
              <Autocomplete
                options={agentOptions}
                getOptionLabel={(option) =>
                  typeof option === 'string'
                    ? option
                    : `${option.firstName || ''} ${option.lastName || ''}`
                }
                isOptionEqualToValue={(option, value) => {
                  if (!option || !value) return false;
                  if (typeof option === 'string' && typeof value === 'string') {
                    return option === value;
                  }
                  if (typeof option === 'object' && typeof value === 'object') {
                    return option.id === value.id;
                  }
                  return false;
                }}
                renderOption={(props, option) => {
                  const { key, ...otherProps } = props;
                  return (
                    <li key={option.id} {...otherProps}>
                      {option.firstName} {option.lastName}
                    </li>
                  );
                }}
                onInputChange={(e, newInputValue) => {
                  setCreatedBySearchTerm(newInputValue);
                  handleCreatedBySearch(newInputValue);
                }}
                onChange={(e, newValue) => {
                  setFilters((prev) => ({
                    ...prev,
                    createdBy: newValue || '' 
                  }));
                }}
                value={filters.createdBy || null}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size={isMobile ? "small" : "medium"}
                    placeholder="Search User"
                    sx={{
                      width: '100%',
                      '& .MuiInputBase-root': {
                        height: isMobile ? '40px' : '55px'
                      }
                    }}
                  />
                )}
              />
            </Grid>

            {/* Other dropdown fields */}
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <label style={{ fontSize: 14, fontWeight: 500, display: 'block', marginBottom: 4 }}>
                  Lead Source
                </label>
                <Autocomplete
                  size={isMobile ? "small" : "medium"}
                  options={["All", "Website", "Referral"]}
                  value={filters.leadSource}
                  onChange={(event, newValue) => handleChange("leadSource", newValue)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Select Lead Source"
                      sx={{
                        "& .MuiInputBase-root": {
                          height: isMobile ? "40px" : "55px",
                        },
                      }}
                    />
                  )}
                />
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <label style={{ fontSize: 14, fontWeight: 500, display: 'block', marginBottom: 4 }}>
                  Line of Business
                </label>
                <Autocomplete
                  size={isMobile ? "small" : "medium"}
                  options={["All", "Auto", "Home"]}
                  value={filters.lineOfBusiness}
                  onChange={(event, newValue) => handleChange("lineOfBusiness", newValue)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Select Line of Business"
                      sx={{
                        "& .MuiInputBase-root": {
                          height: isMobile ? "40px" : "55px",
                        },
                      }}
                    />
                  )}
                />
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <label style={{ fontSize: 14, fontWeight: 500, display: 'block', marginBottom: 4 }}>
                  Lead Status
                </label>
                <Autocomplete
                  size={isMobile ? "small" : "medium"}
                  options={["All", "New", "Contacted", "Quoted", "Dead"]}
                  value={filters.status}
                  onChange={(event, newValue) => handleChange("status", newValue)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Select Lead Status"
                      sx={{
                        "& .MuiInputBase-root": {
                          height: isMobile ? "40px" : "55px",
                        },
                      }}
                    />
                  )}
                />
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <label style={{ fontSize: 14, fontWeight: 500, display: 'block', marginBottom: 4 }}>
                  Quote Number
                </label>
                <TextField
                  name="quoteNumber"
                  size={isMobile ? "small" : "medium"}
                  value={filters.quoteNumber}
                  onChange={handleFilterChange}
                  placeholder="Enter Quote Number"
                  sx={{
                    "& .MuiInputBase-root": {
                      height: isMobile ? "40px" : "55px",
                    },
                  }}
                />
              </FormControl>
            </Grid>

            {/* Action Buttons */}
            <Grid item xs={12} display="flex" justifyContent="flex-end" gap={2}>
              <Button
                onClick={handleReset}
                variant="outlined"
              >
                Reset
              </Button>

              <Button
                onClick={() => {
                  handleSubmit();
                  handleFilterClose();
                }}
                variant="contained"
              >
                Submit
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Popover>

      {/* Table Section */}
      {!hasSearched ? (
        <Box sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '200px',
          backgroundColor: '#f9f9f9',
          borderRadius: 2,
          border: '1px solid #e0e0e0'
        }}>
          <NoDataFound discription={"Please use the search filters above to fetch and display updated CRM Lead Report data."} />
        </Box>
      ) : (
        <>
          <TableContainer component={Paper} sx={{
            maxHeight: isMobile ? '60vh' : 'none',
            overflow: 'auto',
            width: '100%',
            marginBottom: '16px'
          }}>
            <Table
              stickyHeader
              sx={{
                minWidth: isMobile ? '100%' : '650px',
                '& .MuiTableCell-root': {
                  fontSize: isMobile ? '12px' : '14px',
                  padding: isMobile ? '8px' : '12px',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  maxWidth: isMobile ? '100px' : '200px'
                }
              }}
              aria-label="policy table"
              size={isMobile ? "small" : "medium"}
            >
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  {visibleColumns.leadName && (
                    <TableCell className="styled-table-heading" sx={{
                      fontWeight: 'bold',
                      backgroundColor: '#f5f5f5',
                      position: 'sticky',
                      top: 0,
                      fontSize: isMobile ? '12px' : '14px',
                      padding: isMobile ? '8px' : '12px',
                      cursor: 'pointer'
                    }} onClick={() => handleSort('leadName')}>
                      Lead Name {sortField === 'leadName' && (sortOrder === 'asc' ? '↑' : '↓')}
                    </TableCell>
                  )}
                  {visibleColumns.location && (
                    <TableCell className="styled-table-heading" sx={{
                      fontWeight: 'bold',
                      backgroundColor: '#f5f5f5',
                      position: 'sticky',
                      top: 0,
                      fontSize: isMobile ? '12px' : '14px',
                      padding: isMobile ? '8px' : '12px',
                      cursor: 'pointer'
                    }} onClick={() => handleSort('location')}>
                      Location {sortField === 'location' && (sortOrder === 'asc' ? '↑' : '↓')}
                    </TableCell>
                  )}
                   {visibleColumns.assignedTo && (
                    <TableCell className="styled-table-heading" sx={{
                      fontWeight: 'bold',
                      backgroundColor: '#f5f5f5',
                      position: 'sticky',
                      top: 0,
                      fontSize: isMobile ? '12px' : '14px',
                      padding: isMobile ? '8px' : '12px',
                      cursor: 'pointer'
                    }} onClick={() => handleSort('assignedTo')}>
                      Assigned To {sortField === 'assignedTo' && (sortOrder === 'asc' ? '↑' : '↓')}
                    </TableCell>
                  )}
                  {visibleColumns.leadSource && (
                    <TableCell className="styled-table-heading" sx={{
                      fontWeight: 'bold',
                      backgroundColor: '#f5f5f5',
                      position: 'sticky',
                      top: 0,
                      fontSize: isMobile ? '12px' : '14px',
                      padding: isMobile ? '8px' : '12px',
                      cursor: 'pointer'
                    }} onClick={() => handleSort('leadSource')}>
                      Source {sortField === 'leadSource' && (sortOrder === 'asc' ? '↑' : '↓')}
                    </TableCell>
                  )}
                  {visibleColumns.phoneNumber && (
                    <TableCell className="styled-table-heading" sx={{
                      fontWeight: 'bold',
                      backgroundColor: '#f5f5f5',
                      position: 'sticky',
                      top: 0,
                      fontSize: isMobile ? '12px' : '14px',
                      padding: isMobile ? '8px' : '12px',
                      cursor: 'pointer'
                    }} onClick={() => handleSort('phoneNumber')}>
                      Phone {sortField === 'phoneNumber' && (sortOrder === 'asc' ? '↑' : '↓')}
                    </TableCell>
                  )}
                  {visibleColumns.email && (
                    <TableCell className="styled-table-heading" sx={{
                      fontWeight: 'bold',
                      backgroundColor: '#f5f5f5',
                      position: 'sticky',
                      top: 0,
                      fontSize: isMobile ? '12px' : '14px',
                      padding: isMobile ? '8px' : '12px',
                      cursor: 'pointer'
                    }} onClick={() => handleSort('email')}>
                      Email Address {sortField === 'email' && (sortOrder === 'asc' ? '↑' : '↓')}
                    </TableCell>
                  )}
                  {visibleColumns.status && (
                    <TableCell className="styled-table-heading" sx={{
                      fontWeight: 'bold',
                      backgroundColor: '#f5f5f5',
                      position: 'sticky',
                      top: 0,
                      fontSize: isMobile ? '12px' : '14px',
                      padding: isMobile ? '8px' : '12px',
                      cursor: 'pointer'
                    }} onClick={() => handleSort('status')}>
                      Status {sortField === 'status' && (sortOrder === 'asc' ? '↑' : '↓')}
                    </TableCell>
                  )}
                  
                  {visibleColumns.createdDate && (
                    <TableCell className="styled-table-heading" sx={{
                      fontWeight: 'bold',
                      backgroundColor: '#f5f5f5',
                      position: 'sticky',
                      top: 0,
                      fontSize: isMobile ? '12px' : '14px',
                      padding: isMobile ? '8px' : '12px',
                      cursor: 'pointer'
                    }} onClick={() => handleSort('createdDate')}>
                      Created Date {sortField === 'createdDate' && (sortOrder === 'asc' ? '↑' : '↓')}
                    </TableCell>
                  )}
                  {visibleColumns.updatedDate && (
                    <TableCell className="styled-table-heading" sx={{
                      fontWeight: 'bold',
                      backgroundColor: '#f5f5f5',
                      position: 'sticky',
                      top: 0,
                      fontSize: isMobile ? '12px' : '14px',
                      padding: isMobile ? '8px' : '12px',
                      cursor: 'pointer'
                    }} onClick={() => handleSort('updatedDate')}>
                      Updated Date {sortField === 'updatedDate' && (sortOrder === 'asc' ? '↑' : '↓')}
                    </TableCell>
                  )}
                 
                  {visibleColumns.createdBy && (
                    <TableCell className="styled-table-heading" sx={{
                      fontWeight: 'bold',
                      backgroundColor: '#f5f5f5',
                      position: 'sticky',
                      top: 0,
                      fontSize: isMobile ? '12px' : '14px',
                      padding: isMobile ? '8px' : '12px',
                      cursor: 'pointer'
                    }} onClick={() => handleSort('createdBy')}>
                      Created By {sortField === 'createdBy' && (sortOrder === 'asc' ? '↑' : '↓')}
                    </TableCell>
                  )}
                  {visibleColumns.quoteNumber && (
                    <TableCell className="styled-table-heading" sx={{
                      fontWeight: 'bold',
                      backgroundColor: '#f5f5f5',
                      position: 'sticky',
                      top: 0,
                      fontSize: isMobile ? '12px' : '14px',
                      padding: isMobile ? '8px' : '12px',
                      cursor: 'pointer'
                    }} onClick={() => handleSort('quoteNumber')}>
                      Quote Number {sortField === 'quoteNumber' && (sortOrder === 'asc' ? '↑' : '↓')}
                    </TableCell>
                  )}
                </TableRow>
              </TableHead>

              <TableBody>
                {searchLeads?.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={Object.keys(visibleColumns).filter(col => visibleColumns[col]).length}
                      sx={{ padding: '12px', textAlign: 'center' }}>
                      <NoSearchResults />
                    </TableCell>
                  </TableRow>
                ) : (
                  searchLeads.map((row, idx) => (
                    <TableRow key={idx} className="styled-table-row">
                      {visibleColumns.leadName && <TableCell>{row.leadName}</TableCell>}
                      {visibleColumns.location && <TableCell>{row.location || 'N/A'}</TableCell>}
                      {visibleColumns.assignedTo && <TableCell>{row.assignedToName || 'N/A'}</TableCell>}
                      {visibleColumns.leadSource && <TableCell>{row.leadSource}</TableCell>}
                      {visibleColumns.phoneNumber && <TableCell>{formatPhoneNumber(row.phoneNumber)}</TableCell>}
                      {visibleColumns.email && <TableCell>{row.email}</TableCell>}
                      {visibleColumns.status && <TableCell>{row.status}</TableCell>}                      
                      {visibleColumns.createdDate && <TableCell>{dayjs(row.createdDate).format('DD-MM-YYYY - hh:mm A')}</TableCell>}
                      {visibleColumns.updatedDate && <TableCell>{dayjs(row.updatedDate).format('DD-MM-YYYY - hh:mm A')}</TableCell>}                      
                      {visibleColumns.createdBy && <TableCell>{row.createdByName || 'N/A'}</TableCell>}
                      {visibleColumns.quoteNumber && <TableCell>{row.quoteNumber}</TableCell>}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          {totalElements > 0 && (
            <Box sx={{
              width: '100%',
              overflow: 'hidden',
              padding: '16px 0'
            }}>
              <Stack
                direction={isMobile ? 'column' : 'row'}
                justifyContent="flex-end"
                alignItems="center"
                flexWrap="wrap"
                spacing={2}
              >
                <Box display="flex" alignItems="center">
                  <Typography variant="body2" sx={{ fontSize: '14px', marginRight: '8px' }}>
                    Rows per page:
                  </Typography>
                  <Select
                    value={rowsPerPage}
                    onChange={handleRowsPerPageChange}
                    size="small"
                    sx={{
                      fontSize: isMobile ? '12px' : '14px',
                      minWidth: isMobile ? '70px' : '80px',
                    }}
                  >
                    {[5, 10, 15, 25, 50, 100].map((option) => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </Select>
                </Box>

                {searchLeads && searchLeads.length > 0 && (
                  <Pagination
                    count={totalPages}
                    page={page + 1}
                    onChange={handlePageChange}
                    showFirstButton
                    showLastButton
                    siblingCount={isMobile ? 0 : 1}
                    boundaryCount={isMobile ? 0 : 1}
                    size={isMobile ? "small" : "medium"}
                  />
                )}
              </Stack>
            </Box>
          )}
        </>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CustomerCrmLead;